import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { AlertCircle, ArrowRight, ClipboardList, CreditCard, FileSignature, FolderOpen, Home, Loader2, PartyPopper, UserRound, X } from 'lucide-react';

import { useUser } from '../../../contexts/UserContext';
import { cn } from '../../../lib/utils';
import { useFetchClaim } from '../../../services/claims';
import { useFetchBillingInvoices, useFetchPaymentPlanSummary } from '../../../services/billing';
import { fetchSaSignLink, useDismissCmIntro, useMyOnboarding, useOnboardingStatus } from '../../../services/onboarding';
import BillingTab from '../../profile/components/BillingTab';
import EditClaimModal from '../../claim/EditClaimModal';
import { headlineOf, journeyOf, stillNeeded } from './portal/journey';
import JourneyTracker from './portal/JourneyTracker';
import NowCard from './portal/NowCard';
import MeetingCard from './portal/MeetingCard';
import { BillingContact, ClaimSnapshot, SIDE_CARD, TeamCard } from './portal/SideCards';
import DetailsPanel from './portal/DetailsPanel';
import ClientDocuments from './portal/ClientDocuments';
import ClientAnswers from './portal/ClientAnswers';

// The client's dashboard (Nic 2026-10-10: "make this a usable dashboard for
// the client"). One page that says where they are, what's next, and who is
// helping, with their documents, billing, details and questionnaire a tap
// away. Built for a phone first; two columns on a computer.
//
// The section keys stay the old tab keys (files, billing, current,
// questionnaire), so links like /dashboard?tab=billing (the payment plan
// return) still land in the right place. Billing is the same BillingTab as
// before, unchanged.

// short: the phone's bottom tab bar.
const SECTIONS = [
  { key: 'overview', label: 'Overview', short: 'Home', icon: Home },
  { key: 'files', label: 'Documents', short: 'Documents', icon: FolderOpen },
  { key: 'billing', label: 'Billing', short: 'Billing', icon: CreditCard },
  { key: 'current', label: 'My details', short: 'Details', icon: UserRound },
  { key: 'questionnaire', label: 'Questionnaire', short: 'Answers', icon: ClipboardList },
];

const partOfDay = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

const CLAIM_REFRESH = { refetchInterval: 45000, refetchOnWindowFocus: true };

const ClientDashboard = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [edit, setEdit] = useState(false);
  const claimId = user._currentClaimId;

  useEffect(() => {
    if (!claimId) navigate('/questionnaire');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Their files keep up with their specialist (Nic 2026-10-24: a rename
  // or a step our team filed a file under only showed after a reload). A
  // quiet re-check every 45s while the page is on screen (React Query pauses
  // it in a hidden tab and stops it when they leave; each check replaces the
  // last, nothing piles up), and right away when they come back to the tab.
  const claimQ = useFetchClaim(claimId, CLAIM_REFRESH);
  const onboardingQ = useMyOnboarding();
  // A corrected agreement to sign (Nic 2026-10-21): the 10s status poll says
  // so, so the banner shows up without a refresh. The click fetches a fresh
  // signing link (the poll never rotates it).
  const statusQ = useOnboardingStatus();
  const correctionReady = !!statusQ.data?.correctionReady;
  const [openingSign, setOpeningSign] = useState(false);
  // Meet your Claims Manager (Nic 2026-10-26): gone the moment they close it.
  const [cmIntroClosed, setCmIntroClosed] = useState(false);
  const dismissCmIntro = useDismissCmIntro({ onSettled: () => queryClient.invalidateQueries(['my-onboarding']) });
  // Back from the signer (Nic 2026-10-26, the same as Processing.js): the
  // browser can restore this page from its back-forward cache exactly as it
  // left, still spinning. A restored page gets the banner back and fresh
  // data, in case they signed before coming back.
  useEffect(() => {
    const onShow = (e) => {
      if (!e.persisted) return;
      setOpeningSign(false);
      statusQ.refetch();
      onboardingQ.refetch();
      claimQ.refetch();
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const openCorrection = async () => {
    setOpeningSign(true);
    try {
      const res = await fetchSaSignLink();
      if (res?.signUrl) window.location.assign(res.signUrl);
      else setOpeningSign(false);
    } catch (e) {
      setOpeningSign(false);
      toast.error(e?.response?.data?.msg || "Your agreement couldn't be opened. Try again in a moment.");
    }
  };
  // A client always sees their own billing (the same rule as before).
  const { data: invoices } = useFetchBillingInvoices(claimId, { enabled: !!claimId });
  const { data: planSummary } = useFetchPaymentPlanSummary(claimId, { enabled: !!claimId });

  const keys = SECTIONS.map((s) => s.key);
  const raw = searchParams.get('tab');
  const section = keys.includes(raw) ? raw : 'overview';
  const go = (key) => {
    setSearchParams(key === 'overview' ? {} : { tab: key });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!claimId) return <p className="p-6 text-sm text-slate-500">Redirecting...</p>;
  if (claimQ.isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 size={24} className="animate-spin text-slate-400" />
      </div>
    );
  }
  if (claimQ.isError) {
    return (
      <div className="mx-auto mt-6 max-w-[1160px] rounded-2xl bg-rose-50 p-5 text-sm text-rose-700">
        {claimQ.error?.message || "Your dashboard couldn't be loaded. Please refresh."}
      </div>
    );
  }

  const claim = claimQ.data;
  const onboarding = onboardingQ.data?.state ? onboardingQ.data : null;
  const journey = journeyOf({ claim, onboarding });
  const todoCount = onboarding ? stillNeeded(onboarding.items).length : 0;

  // An invoice covered by a live payment plan (or one whose deposit is
  // clearing) is agreed to be paid: never nag about it (same rule as before).
  const planCovered = new Set(
    (planSummary?.plans || [])
      .filter((plan) => ['active', 'past_due'].includes(plan.status) || plan?.metadata?.depositStatus === 'processing')
      .flatMap((plan) => plan.sourceInvoiceIds || []),
  );
  const openInvoice = (invoices || []).some((inv) => inv.invoiceStatus === 'open' && !planCovered.has(inv.invoiceId));

  // Their team: the Claims Manager once they have one, else their specialist.
  const cm = onboarding?.claimsManager || (journey.now === 'building' || journey.now === 'va' || journey.now === 'complete' || journey.now === 'paid' ? claim.assignedUser : null);
  const specialist = onboarding?.specialist || null;
  const teamPerson = cm || specialist;
  const teamTitle = cm ? 'Your Claims Manager' : 'Your onboarding specialist';
  const nameOf = (p) => (p ? `${p.userFirstName || ''}`.trim() : null);

  return (
    <div className="mx-auto w-full max-w-[1160px] pb-28 pt-2 sm:pb-12 sm:pt-4">
      {/* Greeting */}
      <header className="mb-[16px] sm:mb-[24px]">
        <h1 className="mb-1 text-[26px] font-bold leading-tight tracking-tight text-slate-900 dark:text-white sm:text-[30px]">
          {partOfDay()}
          {user.userFirstName ? `, ${user.userFirstName}` : ''}
        </h1>
        <p className="mb-0 text-[15px] text-slate-500 dark:text-slate-400">{headlineOf(journey, { todoCount })}</p>
      </header>

      {/* Sections (Nic 2026-10-10: the pill row ran off a phone's screen).
          On a computer: one contained segmented bar. On a phone: a tab bar
          fixed to the bottom of the screen, like an app, in thumb's reach. */}
      <nav
        aria-label="Your dashboard"
        className="mb-6 hidden w-full rounded-2xl bg-white p-1 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-slate-900 dark:ring-1 dark:ring-white/10 sm:flex"
      >
        {SECTIONS.map((s) => {
          const on = section === s.key;
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => go(s.key)}
              aria-current={on ? 'page' : undefined}
              className={cn(
                'relative inline-flex h-10 min-w-0 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-2 text-sm font-semibold outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
                on
                  ? 'bg-slate-900 text-white shadow-sm dark:!bg-white dark:!text-slate-900'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:!bg-white/5 dark:hover:text-white',
              )}
            >
              <Icon size={16} className="hidden shrink-0 md:block" />
              <span className="truncate">{s.label}</span>
              {s.key === 'billing' && openInvoice ? (
                <span aria-label="Invoice due" className="size-2 shrink-0 rounded-full bg-rose-500" />
              ) : null}
            </button>
          );
        })}
      </nav>
      <nav
        aria-label="Your dashboard"
        className="fixed inset-x-0 bottom-0 z-[1030] grid grid-cols-5 border-t-[1px] border-slate-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur dark:!border-white/10 dark:!bg-slate-900/95 sm:hidden"
      >
        {SECTIONS.map((s) => {
          const on = section === s.key;
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => go(s.key)}
              aria-current={on ? 'page' : undefined}
              className={cn(
                'relative flex h-[60px] flex-col items-center justify-center gap-1 text-[10.5px] font-semibold outline-none focus:outline-none',
                on ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500',
              )}
            >
              {on ? (
                <span aria-hidden className="absolute inset-x-5 top-0 h-[3px] rounded-b-full bg-slate-900 dark:!bg-white" />
              ) : null}
              <span className="relative">
                <Icon size={21} strokeWidth={on ? 2.4 : 2} />
                {s.key === 'billing' && openInvoice ? (
                  <span
                    aria-label="Invoice due"
                    className="absolute -right-1 -top-0.5 size-2.5 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900"
                  />
                ) : null}
              </span>
              {s.short}
            </button>
          );
        })}
      </nav>

      {/* A corrected agreement to sign (Nic 2026-10-21): on top of every tab,
          appearing on its own within seconds (the status poll), and the
          button opens the signing page with a fresh link. */}
      {correctionReady ? (
        <button
          type="button"
          disabled={openingSign}
          onClick={openCorrection}
          data-testid="sign-corrected-agreement"
          className="mb-5 flex w-full items-center gap-3 rounded-2xl bg-amber-50 px-4 py-3.5 text-left outline-none ring-1 ring-amber-200 hover:bg-amber-100/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 disabled:opacity-60 dark:!bg-amber-500/10 dark:ring-amber-500/30"
        >
          <FileSignature size={20} className="shrink-0 text-amber-600 dark:text-amber-300" />
          <span className="min-w-0 flex-1 text-sm">
            <span className="font-bold text-amber-900 dark:text-amber-100">Please sign your updated Service Agreement.</span>{' '}
            <span className="text-amber-800/80 dark:text-amber-200/80">
              We corrected a few details. Your signed agreement stays in place until you sign this one.
            </span>
          </span>
          {openingSign ? (
            <Loader2 size={18} className="shrink-0 animate-spin text-amber-600 dark:text-amber-300" />
          ) : (
            <ArrowRight size={18} className="shrink-0 text-amber-600 dark:text-amber-300" />
          )}
        </button>
      ) : null}

      {/* Their Claims Manager is assigned (Nic 2026-10-26): no contact
          details here (their card has them), and closed for good with the X. */}
      {onboardingQ.data?.cmIntro && !cmIntroClosed ? (
        <div
          data-testid="cm-intro-banner"
          className="mb-5 flex w-full items-center gap-3 rounded-2xl bg-emerald-50 px-4 py-3.5 ring-1 ring-emerald-200 dark:!bg-emerald-500/10 dark:ring-emerald-500/30"
        >
          <PartyPopper size={20} className="shrink-0 text-emerald-600 dark:text-emerald-300" />
          <span className="min-w-0 flex-1 text-sm">
            <span className="font-bold text-emerald-900 dark:text-emerald-100">You&apos;re assigned a new Claims Manager!</span>{' '}
            <span className="text-emerald-800/80 dark:text-emerald-200/80">
              {onboardingQ.data.cmIntro.cmFirstName || 'They'} will reach out to you to introduce themselves.
            </span>
          </span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => {
              setCmIntroClosed(true);
              dismissCmIntro.mutate();
            }}
            className="flex size-8 shrink-0 items-center justify-center rounded-lg text-emerald-700 outline-none hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:text-emerald-300 dark:hover:!bg-emerald-500/20"
          >
            <X size={16} />
          </button>
        </div>
      ) : null}

      {/* An invoice to pay: one clear line that takes them there. */}
      {openInvoice && section !== 'billing' ? (
        <button
          type="button"
          onClick={() => go('billing')}
          className="mb-5 flex w-full items-center gap-3 rounded-2xl bg-rose-50 px-4 py-3.5 text-left outline-none ring-1 ring-rose-200 hover:bg-rose-100/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 dark:!bg-rose-500/10 dark:ring-rose-500/30"
        >
          <AlertCircle size={20} className="shrink-0 text-rose-600 dark:text-rose-300" />
          <span className="min-w-0 flex-1 text-sm">
            <span className="font-bold text-rose-900 dark:text-rose-100">You have an invoice ready.</span>{' '}
            <span className="text-rose-800/80 dark:text-rose-200/80">Review it and pay in Billing.</span>
          </span>
          <ArrowRight size={18} className="shrink-0 text-rose-600 dark:text-rose-300" />
        </button>
      ) : null}

      {section === 'overview' ? (
        <div className="space-y-4 sm:space-y-5">
          <JourneyTracker journey={journey} />
          <div className="grid items-start gap-4 sm:gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
            <NowCard
              journey={journey}
              onboarding={onboarding}
              cmName={cm ? `${cm.userFirstName || ''} ${cm.userLastName || ''}`.trim() : null}
              specialistName={nameOf(specialist)}
              todoCount={todoCount}
              openInvoice={openInvoice}
              onGo={go}
            />
            <div className="space-y-4 sm:space-y-5">
              {/* Their next call (in-app meetings, 2026-10-25), first when there is one. */}
              <MeetingCard meeting={onboardingQ.data?.meeting || null} />
              <TeamCard
                title={teamTitle}
                person={teamPerson}
                waiting={
                  cm === null && journey.now === 'matching'
                    ? 'Your Claims Manager will reach out soon.'
                    : onboarding?.specialistPending
                      ? "We're connecting you with your onboarding specialist now."
                      : 'Your team will reach out soon.'
                }
              />
              <ClaimSnapshot claim={claim} />
              <section className={cn(SIDE_CARD, 'flex flex-col gap-1')}>
                {[
                  { key: 'files', label: 'Your documents', hint: `${(claim.files || []).length} on file`, icon: FolderOpen },
                  { key: 'current', label: 'My details', hint: 'Contact, family, service', icon: UserRound },
                ].map((l) => (
                  <button
                    key={l.key}
                    type="button"
                    onClick={() => go(l.key)}
                    className="-mx-2 flex items-center gap-3 rounded-xl px-2 py-2 text-left outline-none hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:hover:!bg-white/5"
                  >
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 dark:!bg-white/10 dark:text-slate-300">
                      <l.icon size={17} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">{l.label}</span>
                      <span className="block text-xs text-slate-500 dark:text-slate-400">{l.hint}</span>
                    </span>
                    <ArrowRight size={16} className="shrink-0 text-slate-400" />
                  </button>
                ))}
              </section>
            </div>
          </div>
        </div>
      ) : null}

      {/* Documents always opens (Nic 2026-10-21): the questionnaire is its own
          tab, never a gate in front of their files. */}
      {section === 'files' ? (
          <ClientDocuments
            claimId={claim.claimId}
            files={claim.files || []}
            // Their checklist's documents, for "What is this?" (Nic 2026-10-23).
            // Only when this is their onboarding claim (QA 2026-10-26): a file
            // labeled for the checklist must land where the checklist counts it.
            items={
              onboarding?.claimId === claim.claimId
                ? (onboarding?.items || []).filter((i) => i.key !== 'va_gov' && !i.auto)
                : []
            }
            onRefresh={() => {
              queryClient.invalidateQueries('claim');
              queryClient.invalidateQueries('files');
              queryClient.invalidateQueries(['my-onboarding']);
            }}
          />
      ) : null}

      {/* Billing: the same tab as before, unchanged (Nic 2026-10-05), with who
          to ask beside it (Nic 2026-10-26): on a computer in the right column,
          on a phone below. */}
      {section === 'billing' ? (
        <div className="grid items-start gap-4 sm:gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
          <section className={cn(SIDE_CARD, 'min-w-0')}>
            <BillingTab
              claimId={claim.claimId}
              invoices={invoices}
              isAdmin={false}
              billingFrozen={['dead', 'dispute hold'].includes(claim.claimStatus)}
              disputeHold={
                claim.claimStatus === 'dispute hold'
                  ? { at: claim.disputeHoldAt, source: claim.disputeHoldSource, note: claim.disputeHoldNote }
                  : null
              }
            />
          </section>
          <BillingContact />
        </div>
      ) : null}

      {section === 'current' ? <DetailsPanel claim={claim} onEdit={() => setEdit(true)} /> : null}

      {section === 'questionnaire' ? <ClientAnswers claimId={claim.claimId} answers={claim.questionnaire} /> : null}

      <EditClaimModal
        show={edit}
        toggle={() => setEdit(false)}
        claim={claim}
        onSuccess={() => queryClient.invalidateQueries('claim')}
      />
    </div>
  );
};

export default ClientDashboard;
